import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';

import { InstantEstimateService } from './instant-estimate.service';
import {
  InstantEstimateConfig,
  InstantEstimateDisplayMode,
  InstantEstimateResult,
} from './instant-estimate.models';
import { roundToNearest100 } from './instant-estimate.math';

interface AxisTick {
  value: number;
  x?: number;
  y?: number;
  label: string;
}

@Component({
  selector: 'app-instant-estimate-chart',
  standalone: true,
  imports: [CurrencyPipe],
  templateUrl: './instant-estimate-chart.component.html',
  styleUrl: './instant-estimate-chart.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InstantEstimateChartComponent {
  private readonly estimateService = inject(InstantEstimateService);

  readonly config = input.required<InstantEstimateConfig>();
  readonly result = input.required<InstantEstimateResult>();
  readonly displayMode = input<InstantEstimateDisplayMode>('full');
  readonly accentColor = input('#002999');

  readonly chartWidth = 680;
  readonly chartHeight = computed(() =>
    this.displayMode() === 'compact' ? 200 : 300
  );

  private readonly pad = computed(() => ({
    top: 20,
    right: this.displayMode() === 'compact' ? 18 : 24,
    bottom: 58,
    left: 72,
  }));

  readonly series = computed(() =>
    this.estimateService.getChartSeries(this.config())
  );

  readonly ariaLabel = computed(() => {
    const r = this.result();
    const coa = roundToNearest100(r.costOfAttendance);
    return (
      `Net price estimate chart for ${this.config().schoolName}. ` +
      `At family income ${r.income}, estimated net price ${r.central} per year, ` +
      `likely range ${r.rangeLow} to ${r.rangeHigh}, published cost ${coa}.`
    );
  });

  readonly paths = computed(() => {
    const series = this.series();
    const coa = this.config().costOfAttendance;
    const dataMax = Math.max(coa, ...series.rangeHigh, 1);
    const yMax = this.scaleMaxAbove(dataMax);
    const pad = this.pad();
    const width = this.chartWidth;
    const height = this.chartHeight();
    const innerW = width - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;
    const result = this.result();

    const xAt = (income: number) => {
      const t =
        (income - series.incomes[0]) /
        (series.incomes[series.incomes.length - 1] - series.incomes[0]);
      return pad.left + t * innerW;
    };
    const yAt = (value: number) => pad.top + (1 - value / yMax) * innerH;

    const linePath = (values: number[]) =>
      values
        .map((v, i) => {
          const cmd = i === 0 ? 'M' : 'L';
          return `${cmd}${xAt(series.incomes[i]).toFixed(1)} ${yAt(v).toFixed(1)}`;
        })
        .join(' ');

    const bandTop = linePath(series.rangeHigh);
    const bandBottom = [...series.rangeLow]
      .reverse()
      .map((v, i) => {
        const income = series.incomes[series.incomes.length - 1 - i];
        return `L${xAt(income).toFixed(1)} ${yAt(v).toFixed(1)}`;
      })
      .join(' ');
    const bandPath = `${bandTop} ${bandBottom} Z`;

    const selectedX = xAt(result.income);
    const selectedY = yAt(result.central);
    const coaY = yAt(coa);
    const coaDisplay = roundToNearest100(coa);

    const yTicks = this.buildYTicks(yMax).map((value) => ({
      value,
      y: yAt(value),
      label: this.formatAxisCurrency(value),
    })) satisfies AxisTick[];

    const xTickIncomes = [30000, 100000, 200000, 300000];
    const xTicks = xTickIncomes.map((value, index) => {
      const x = xAt(value);
      return {
        value,
        // Nudge first/last labels inward so they don't collide with axes
        x:
          index === 0
            ? x + 10
            : index === xTickIncomes.length - 1
              ? x - 10
              : x,
        label: this.formatAxisIncome(value),
      };
    }) satisfies AxisTick[];

    // Prefer callout to the right of the point; flip left near the right edge.
    // Keep callout inside the plot so it never clips the SVG edge.
    const calloutWidth = 148;
    const calloutHeight = 52;
    const calloutGap = 14;
    const minX = pad.left + 4;
    const maxX = width - pad.right - calloutWidth - 4;
    let calloutX = selectedX + calloutGap;
    if (calloutX > maxX) {
      calloutX = selectedX - calloutGap - calloutWidth;
    }
    calloutX = Math.min(maxX, Math.max(minX, calloutX));

    // Prefer above the point when near the bottom (low net price)
    let calloutY = selectedY - calloutHeight - 10;
    if (calloutY < pad.top) {
      calloutY = Math.min(
        selectedY + 10,
        height - pad.bottom - calloutHeight
      );
    }
    calloutY = Math.min(
      Math.max(calloutY, pad.top),
      height - pad.bottom - calloutHeight
    );

    // COA label on the right, slightly above the dashed line
    const coaLabelX = width - pad.right - 8;
    const coaLabelY = Math.max(coaY - 6, pad.top + 12);

    return {
      bandPath,
      rangeHighPath: linePath(series.rangeHigh),
      rangeLowPath: linePath(series.rangeLow),
      centralPath: linePath(series.central),
      selectedX,
      selectedY,
      coaY,
      coaDisplay,
      coaLabelX,
      coaLabelY,
      plotLeft: pad.left,
      plotRight: width - pad.right,
      plotTop: pad.top,
      plotBottom: height - pad.bottom,
      yTicks,
      xTicks,
      calloutX,
      calloutY,
      calloutWidth,
      calloutHeight,
    };
  });

  /**
   * Cap the axis just above the highest data value (usually COA),
   * snapped to a round step so tick labels stay clean ($25k, $50k, …).
   */
  private scaleMaxAbove(value: number): number {
    const padded = value * 1.08;
    const step = this.niceStep(padded);
    return Math.ceil(padded / step) * step;
  }

  private niceStep(span: number): number {
    if (span <= 40_000) return 10_000;
    if (span <= 80_000) return 20_000;
    if (span <= 160_000) return 25_000;
    return 50_000;
  }

  private buildYTicks(yMax: number): number[] {
    const step = this.niceStep(yMax);
    const ticks: number[] = [];
    for (let v = 0; v < yMax; v += step) {
      ticks.push(v);
    }
    ticks.push(yMax);
    return ticks;
  }

  private formatAxisCurrency(value: number): string {
    if (value >= 1000) {
      const thousands = value / 1000;
      // Prefer whole thousands for axis labels ($25k not $27.5k)
      if (Math.abs(thousands - Math.round(thousands)) < 0.05) {
        return `$${Math.round(thousands)}k`;
      }
      return `$${Math.round(thousands * 10) / 10}k`;
    }
    return `$${value}`;
  }

  private formatAxisIncome(value: number): string {
    if (value >= 1000) {
      return `$${Math.round(value / 1000)}k`;
    }
    return `$${value}`;
  }
}
